import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardLayout from './components/layout/DashboardLayout';
import CalendarPanel from './components/panels/CalendarPanel';
import AlgorithmDashboard from './components/panels/AlgorithmDashboard';
import SchedulingPage from './components/panels/SchedulingPage';
import EntitiesPanel from './components/panels/EntitiesPanel';
import AssignmentsPanel from './components/panels/AssignmentsPanel';
import StudentsPanel from './components/panels/StudentsPanel';
import AttendanceManagerPortal from './components/panels/AttendanceManagerPortal';
import ContentExplorerPanel from './components/panels/ContentExplorerPanel';
import DocumentProcessingPanel from './components/panels/DocumentProcessingPanel';
import SettingsPanel from './components/panels/SettingsPanel';
import BenchrexPanel from './components/panels/BenchrexPanel';
import MainDashboardPanel from './components/panels/MainDashboardPanel';
import InquiriesPanel from './components/panels/InquiriesPanel';
import BillingPricingPanel from './components/panels/BillingPricingPanel';
import PublicAttendancePage from './pages/PublicAttendancePage';
import PublicTestPage from './pages/PublicTestPage';
import DocsPage from './pages/DocsPage';
import PrivacyPage from './pages/PrivacyPage';
import LicensePage from './pages/LicensePage';
import FeaturesPage from './pages/FeaturesPage';
import SolutionsPage from './pages/SolutionsPage';
import SecurityPage from './pages/SecurityPage';
import BenchrexLandingPage from './pages/BenchrexLandingPage';
import AboutPage from './pages/AboutPage';
import { TooltipProvider } from './components/ui/tooltip';
import { supabase } from './lib/supabase';
import useCalendarStore from './store/useCalendarStore';
import BackendConnectionProgress from './components/BackendConnectionProgress';


// CalendarSync Student Portal (Removed - Hosted Separately)

function PrivateRoute({ children }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'authed' | 'anon'

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setStatus(session ? 'authed' : 'anon');
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setStatus(session ? 'authed' : 'anon');
    });
    return () => subscription.unsubscribe();
  }, []);

  if (status === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center bg-zinc-50 dark:bg-zinc-950">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (status === 'anon') return <Navigate to="/login" replace />;
  return children;
}

function LayoutScaler() {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const prevFontSize = document.documentElement.style.fontSize;
    const isLanding = location.pathname === '/';
    const isDashboard = location.pathname.startsWith('/dashboard');

    if (isMobile) {
      document.documentElement.style.fontSize = '16px';
    } else if (isLanding) {
      document.documentElement.style.fontSize = '18px';
    } else if (isDashboard) {
      document.documentElement.style.fontSize = '18px';
    } else {
      document.documentElement.style.fontSize = '18px';
    }

    return () => {
      document.documentElement.style.fontSize = prevFontSize || '';
    };
  }, [location.pathname, isMobile]);

  return null;
}

function StudentsPanelSelector() {
  const [role, setRole] = useState('viewer');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
      if (profile.role) {
        setRole(profile.role);
      } else if (profile.access_level) {
        const levelToRole = {
          1: 'super_admin', 2: 'branch_admin', 3: 'batch_admin', 4: 'course_admin',
          5: 'content_manager', 6: 'inquiry_admin', 7: 'attendance_manager',
          8: 'faculty', 9: 'viewer', 10: 'doubt_expert'
        };
        setRole(levelToRole[profile.access_level] || 'viewer');
      }
    } catch {
      // ignore
    }
    setLoading(false);
  }, []);

  if (loading) return null;
  if (role === 'attendance_manager') {
    return <AttendanceManagerPortal />;
  }
  return <StudentsPanel />;
}

export default function App() {
  const darkMode = useCalendarStore(s => s.darkMode);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>

      <TooltipProvider>
        <BackendConnectionProgress />
        <LayoutScaler />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/license" element={<LicensePage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/solutions" element={<SolutionsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/portal" element={<BenchrexLandingPage />} />
          <Route path="/attendance" element={<PublicAttendancePage />} />
          <Route path="/tests" element={<PublicTestPage />} />
          <Route path="/scheduling" element={<PrivateRoute><SchedulingPage /></PrivateRoute>} />
          <Route path="/billing" element={<PrivateRoute><BillingPricingPanel /></PrivateRoute>} />

          {/* Authenticated Dashboard (Nested Routes) */}
          <Route path="/dashboard" element={<PrivateRoute><DashboardLayout /></PrivateRoute>}>
            <Route index element={<Navigate to="main" replace />} />
            <Route path="main" element={<MainDashboardPanel />} />
            <Route path="calendar" element={<CalendarPanel />} />
            <Route path="algorithms" element={<AlgorithmDashboard />} />
            <Route path="entities" element={<EntitiesPanel />} />
            <Route path="branches" element={<Navigate to="/dashboard/entities" replace />} />
            <Route path="faculty" element={<Navigate to="/dashboard/entities" replace />} />
            <Route path="courses" element={<Navigate to="/dashboard/entities" replace />} />
            <Route path="batches" element={<Navigate to="/dashboard/entities" replace />} />
            <Route path="assignments" element={<AssignmentsPanel />} />
            <Route path="students" element={<StudentsPanelSelector />} />
            <Route path="explorer" element={<ContentExplorerPanel />} />
            <Route path="inquiries" element={<InquiriesPanel />} />
            <Route path="settings" element={<SettingsPanel />} />
            <Route path="benchrex" element={<BenchrexPanel />} />
          </Route>

          {/* Fallback: redirect old "/" dashboard access */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors closeButton />
      </TooltipProvider>
    </BrowserRouter>
  );
}
